import { useState, useCallback, useRef } from "react";
import { resumeAgent, streamAgent, type SSECallbacks } from "@/api/agents";
import { trackEvent } from "@/lib/analytics";
import { STREAM_INTERRUPTED_CODE, STREAM_TRANSPORT_ERROR_CODE } from "@/lib/stream-error-codes";
import { telemetry } from "@/lib/telemetry";
import type {
  AgentExecution,
  AgentStreamRequest,
  StreamEventType,
  StartedEvent,
  PhaseEvent,
  TokenEvent,
  CompleteEvent,
  ErrorEvent,
  RagCompleteEvent,
  WarningEvent,
  WarningEntry,
  HumanInputRequiredEvent,
  Citation,
  ExecutionMetrics,
} from "@/types/api";

export type ExecutionStatus =
  | "idle"
  | "running"
  | "paused"
  | "completed"
  | "failed";

// As constantes moram em `lib/` para que módulos de mensagens não dependam do hook
// (e dos testes que o substituem por um mock). O hook as reexporta.
export { STREAM_INTERRUPTED_CODE, STREAM_TRANSPORT_ERROR_CODE };

/** Map SSE phase strings to stepper indices.
 *  Includes both generic phases and Functional custom phases. */
const PHASE_INDEX: Record<string, number> = {
  // Generic phases
  pending: 0,
  validating: 0,
  retrieving: 1,
  reranking: 2,
  executing_tools: 3,
  generating: 3,
  validating_output: 4,
  formatting: 5,
  completed: 6,
  failed: 6,
  // Functional custom phases
  extract_ecc_data: 0,
  classify_modules: 1,
  map_scope_items: 2,
  resolve_skus: 3,
  generate_bom: 4,
  generate_report: 5,
};

export type ActivityEventLevel = "I" | "W" | "E";

export interface ActivityEventState {
  ts: string;
  node?: string;
  tx: string;
  lvl: ActivityEventLevel;
  current?: boolean;
  pending?: boolean;
}

export interface AgentExecutionState {
  status: ExecutionStatus;
  executionId: string | null;
  phase: string;
  phaseIndex: number;
  phaseMessage: string;
  tokens: string;
  outputs: Record<string, unknown> | null;
  citations: Citation[];
  metrics: ExecutionMetrics | null;
  chunksRetrieved: number;
  error: string | null;
  errorCode?: string;
  warnings: WarningEntry[];
  activity: ActivityEventState[];
  startedAt: number | null;
  /**
   * Configuration Server: artifact `draft` da run em voo, entregue pelos
   * eventos `phase` assim que o nó 1 termina. `outputs.artifact_id` só
   * chega no `complete` — tarde demais para as abas mostrarem progresso.
   * Fica `null` para os agentes que não emitem o campo.
   */
  liveArtifactId: string | null;
  /**
   * Human-in-the-Loop pause state (S-021).
   *
   * Set when the agent's graph emits `interrupt(payload)` and the
   * backend forwards a `human_input_required` SSE event. The UI reads
   * `payload` to render an input form via `AgentInputForm`. Call
   * `resume(humanResponse)` to close the pause.
   *
   * Cleared when `resume()` is invoked or a fresh `execute()` starts.
   */
  humanInputRequest: HumanInputRequiredEvent | null;
}

const initialState: AgentExecutionState = {
  status: "idle",
  executionId: null,
  phase: "",
  phaseIndex: -1,
  phaseMessage: "",
  tokens: "",
  outputs: null,
  citations: [],
  metrics: null,
  chunksRetrieved: 0,
  error: null,
  warnings: [],
  activity: [],
  startedAt: null,
  liveArtifactId: null,
  humanInputRequest: null,
};

const ACTIVITY_CAP = 200;

function hhmmss(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const PHASE_TO_NODE_NUM: Record<string, string> = {
  // Functional custom phases
  extract_ecc_data: "01",
  classify_modules: "02",
  map_scope_items: "03",
  resolve_skus: "04",
  generate_bom: "05",
  generate_report: "06",
};

function nodeNumForPhase(phase: string, idx: number | undefined): string | undefined {
  if (PHASE_TO_NODE_NUM[phase]) return PHASE_TO_NODE_NUM[phase];
  if (idx == null || idx < 0) return undefined;
  return String(idx + 1).padStart(2, "0");
}

function appendActivity(
  prev: ActivityEventState[],
  next: ActivityEventState,
): ActivityEventState[] {
  const updated = prev.map((e) => (e.current ? { ...e, current: false } : e));
  updated.push(next);
  if (updated.length > ACTIVITY_CAP) {
    return updated.slice(updated.length - ACTIVITY_CAP);
  }
  return updated;
}

export function useAgentExecution() {
  const [state, setState] = useState<AgentExecutionState>(initialState);
  const controllerRef = useRef<AbortController | null>(null);
  const streamGenerationRef = useRef(0);
  const activeExecutionRef = useRef<{
    projectId: string;
    agentId: string;
    executionId: string | null;
    startedAt: number;
    terminalState: "running" | "completed" | "failed" | "cancelled";
  } | null>(null);

  const finalizeExecution = useCallback((
    outcome: "completed" | "failed" | "cancelled",
    properties?: Record<string, unknown>,
  ) => {
    const activeExecution = activeExecutionRef.current;
    if (!activeExecution || activeExecution.terminalState !== "running") {
      return;
    }

    activeExecution.terminalState = outcome;
    trackEvent(`agent_execution_${outcome}`, {
      project_id: activeExecution.projectId,
      agent_id: activeExecution.agentId,
      duration_ms: Date.now() - activeExecution.startedAt,
      ...properties,
    });
  }, []);

  // Single SSE event handler shared by `execute` (initial run) and
  // `resume` (HITL continuation). The two paths emit the same events
  // — keeping one handler avoids drift if a future event type is added.
  const handleSseEvent = useCallback(
    (event: StreamEventType, data: unknown) => {
      switch (event) {
            case "started": {
              const d = data as StartedEvent;
              if (activeExecutionRef.current) {
                activeExecutionRef.current.executionId = d.execution_id;
              }
              setState((s) => ({
                ...s,
                executionId: d.execution_id,
                activity: appendActivity(s.activity, {
                  ts: hhmmss(),
                  tx: `Execution started · ${d.execution_id.slice(0, 8)}`,
                  lvl: "I",
                }),
              }));
              break;
            }
            case "phase": {
              const d = data as PhaseEvent;
              setState((s) => {
                const nextIdx = PHASE_INDEX[d.phase] ?? s.phaseIndex;
                return {
                  ...s,
                  phase: d.phase,
                  phaseIndex: nextIdx,
                  phaseMessage: d.message || "",
                  // Um `phase` sem artifact não apaga o id já conhecido —
                  // nem quando o campo vem ausente, nem `null`. `PhaseEvent`
                  // declara `artifact_id` com default `None`, então TODO
                  // evento serializa a chave, inclusive os emissores que não
                  // a preenchem; sem o `!= null` (frouxo, pega os dois) as
                  // abas perderiam o draft no meio da run.
                  liveArtifactId:
                    d.artifact_id != null ? d.artifact_id : s.liveArtifactId,
                  activity: appendActivity(s.activity, {
                    ts: hhmmss(),
                    node: nodeNumForPhase(d.phase, nextIdx),
                    tx: d.message || d.phase,
                    lvl: "I",
                    current: true,
                  }),
                };
              });
              break;
            }
            case "rag_complete": {
              const d = data as RagCompleteEvent;
              setState((s) => ({ ...s, chunksRetrieved: d.chunks_retrieved }));
              break;
            }
            case "token": {
              const d = data as TokenEvent;
              if (d.token != null) {
                setState((s) => ({ ...s, tokens: s.tokens + d.token }));
              }
              break;
            }
            case "citations_validated": {
              // Optional: could capture citations_count here
              break;
            }
            case "warning": {
              // Avisos estruturados (code+params, um dos 5 do
              // ambiente de demo) preservam code/params para o WarningsBanner
              // traduzir; os demais continuam string solta, como sempre.
              const d = data as WarningEvent;
              const entry: WarningEntry = d.code
                ? { node: d.source, code: d.code, message: d.message, params: d.params }
                : d.message;
              setState((s) => ({
                ...s,
                warnings: [...s.warnings, entry],
                activity: appendActivity(s.activity, {
                  ts: hhmmss(),
                  tx: d.message,
                  lvl: "W",
                }),
              }));
              break;
            }
            case "human_input_required": {
              const d = data as HumanInputRequiredEvent;
              setState((s) => ({
                ...s,
                status: "paused",
                humanInputRequest: d,
                activity: appendActivity(s.activity, {
                  ts: hhmmss(),
                  node: nodeNumForPhase(d.node_name, s.phaseIndex),
                  tx: `Waiting for human input: ${d.node_name}`,
                  lvl: "W",
                }),
              }));
              break;
            }
            case "complete": {
              const d = data as CompleteEvent;
              finalizeExecution("completed", {
                execution_id: d.execution_id,
                total_tokens: d.metrics?.total_tokens,
                cost_estimate: d.metrics?.cost_estimate,
              });
              setState((s) => ({
                ...s,
                status: "completed",
                executionId: d.execution_id ?? s.executionId,
                outputs: d.outputs,
                citations: d.citations || [],
                metrics: d.metrics || null,
                phase: "completed",
                phaseIndex: 6,
                activity: appendActivity(s.activity, {
                  ts: hhmmss(),
                  tx: "Execution completed",
                  lvl: "I",
                }),
              }));
              break;
            }
        case "error": {
          const d = data as ErrorEvent;
          finalizeExecution("failed", { error: d.error });
          setState((s) => ({
            ...s,
            status: "failed",
            error: d.error,
            phase: "failed",
            activity: appendActivity(s.activity, {
              ts: hhmmss(),
              tx: d.error,
              lvl: "E",
            }),
          }));
          break;
        }
      }
    },
    [finalizeExecution],
  );

  const handleSseError = useCallback(
    (err: Error) => {
      // Falha HTTP carrega `status` numérico; sem ele a conexão caiu antes de haver resposta.
      const status = (err as Error & { status?: unknown }).status;
      const code =
        (err as Error & { code?: string }).code ??
        (typeof status === "number" ? undefined : STREAM_TRANSPORT_ERROR_CODE);
      finalizeExecution("failed", { error: err.message, errorCode: code });
      setState((s) => ({
        ...s,
        status: "failed",
        error: err.message,
        errorCode: code,
        activity: appendActivity(s.activity, {
          ts: hhmmss(),
          tx: err.message,
          lvl: "E",
        }),
      }));
    },
    [finalizeExecution],
  );

  // Streams antigos podem terminar depois de outra execução começar. Cada
  // transporte só altera o estado e a telemetria da geração que o abriu.
  const nextStreamCallbacks = useCallback((): SSECallbacks => {
    const generation = ++streamGenerationRef.current;
    // Fim do body sem evento final é interrupção: o agents caiu, a conexão
    // caiu ou o servidor encerrou. Sem isto a tela ficava "em andamento".
    let finished = false;
    return {
      onEvent: (event, data) => {
        if (generation !== streamGenerationRef.current) return;
        if (event === "complete" || event === "error" || event === "human_input_required") finished = true;
        handleSseEvent(event, data);
      },
      onError: (error) => {
        if (generation === streamGenerationRef.current) handleSseError(error);
      },
      onDone: () => {
        if (generation !== streamGenerationRef.current || finished) return;
        const active = activeExecutionRef.current;
        const error = Object.assign(new Error("The run was interrupted before it finished."), {
          code: STREAM_INTERRUPTED_CODE,
        });
        handleSseError(error);
        try {
          telemetry.captureWarning("sse", "stream ended without terminal event", {
            executionId: active?.executionId ?? null,
            agentId: active?.agentId,
            projectId: active?.projectId,
          });
        } catch {
          // Telemetria não pode trocar o erro da execução.
        }
      },
    };
  }, [handleSseEvent, handleSseError]);

  const execute = useCallback(
    (projectId: string, agentId: string, request: AgentStreamRequest) => {
      const startedAt = Date.now();
      setState({ ...initialState, status: "running", phaseIndex: 0, startedAt });
      activeExecutionRef.current = {
        projectId,
        agentId,
        executionId: null,
        startedAt,
        terminalState: "running",
      };
      trackEvent("agent_execution_started", {
        project_id: projectId,
        agent_id: agentId,
        context_field_count: Object.keys(request.context ?? {}).length,
        has_thread_id: !!request.thread_id,
      });

      const controller = streamAgent(projectId, agentId, request, nextStreamCallbacks());

      controllerRef.current = controller;
    },
    [nextStreamCallbacks],
  );

  /**
   * Resume a paused agent execution (S-021 HITL).
   *
   * Requires an active execution that previously emitted
   * `human_input_required`. The `humanResponse` is forwarded to the
   * agents service which feeds it into `Command(resume=...)`. The SSE
   * continues on the same channel — same event handlers as `execute`.
   *
   * No-ops with a warning if there's no paused execution to resume.
   */
  const resume = useCallback(
    (humanResponse: unknown) => {
      const active = activeExecutionRef.current;
      if (!active || !active.executionId) {
        console.warn(
          "[useAgentExecution] resume called with no active execution",
        );
        return;
      }

      setState((s) => ({
        ...s,
        status: "running",
        humanInputRequest: null,
        activity: appendActivity(s.activity, {
          ts: hhmmss(),
          tx: "Resuming after human input",
          lvl: "I",
        }),
      }));

      trackEvent("agent_execution_resumed", {
        project_id: active.projectId,
        agent_id: active.agentId,
        execution_id: active.executionId,
      });

      const controller = resumeAgent(
        active.projectId,
        active.agentId,
        active.executionId,
        { human_response: humanResponse },
        nextStreamCallbacks(),
      );

      controllerRef.current = controller;
    },
    [nextStreamCallbacks],
  );

  /**
   * Rehydrate state from a paused execution detail (S-021 HITL-4.2).
   *
   * Called by the workbench on F5 when `useExecution(id)` returns a row
   * with `status === "paused"`. Adopts the execution into the hook so
   * the user can hit `Resume` without having lived through the SSE
   * stream that originally paused it.
   *
   * Idempotent: if there's already an active execution (running or
   * paused live), this no-ops. The live state wins — refreshing while
   * the SSE is mid-flight shouldn't be possible anyway.
   *
   * `interrupt_id` is synthesized — the original LangGraph-assigned id
   * isn't stored in the DB. The frontend doesn't need it for resume
   * (the agents service derives the pending interrupt from graph state
   * itself), only for activity correlation. The synthetic value is
   * deterministic per execution so re-hydrations stay stable.
   */
  const hydratePaused = useCallback(
    (projectId: string, agentId: string, detail: AgentExecution) => {
      if (detail.status !== "paused" || !detail.interrupt_payload) return;
      if (activeExecutionRef.current?.terminalState === "running") return;
      ++streamGenerationRef.current;

      const startedAt = detail.started_at
        ? new Date(detail.started_at).getTime()
        : Date.now();

      activeExecutionRef.current = {
        projectId,
        agentId,
        executionId: detail.id,
        startedAt,
        terminalState: "running",
      };

      // S-021 HITL-4 F1: backend persiste `interrupt_node` (migration
      // 0007). Mapeia pra phaseIndex via PHASE_INDEX pra que o rail
      // do workbench mostre o nó certo como `paused` em vez de tudo
      // como `pending`. Quando ausente (rows pré-0007), fallback no
      // payload.node_name (convenção antiga, agentes opcionalmente
      // incluíam) e por último "paused" como last-resort.
      const nodeNameFromDetail =
        detail.interrupt_node ??
        (detail.interrupt_payload?.node_name as string | undefined) ??
        "paused";
      const hydratedPhaseIndex =
        PHASE_INDEX[nodeNameFromDetail] ?? -1;

      setState((s) => ({
        ...s,
        status: "paused",
        executionId: detail.id,
        startedAt,
        phase: nodeNameFromDetail,
        phaseIndex: hydratedPhaseIndex,
        humanInputRequest: {
          execution_id: detail.id,
          node_name: nodeNameFromDetail,
          interrupt_id: `rehydrated-${detail.id.slice(0, 8)}`,
          payload: detail.interrupt_payload as Record<string, unknown>,
        },
        activity: appendActivity(s.activity, {
          ts: hhmmss(),
          tx: `Rehydrated from paused run · ${detail.id.slice(0, 8)}`,
          lvl: "I",
        }),
      }));
    },
    [],
  );

  const cancel = useCallback(() => {
    ++streamGenerationRef.current;
    controllerRef.current?.abort();
    finalizeExecution("cancelled");
    setState((s) => ({ ...s, status: "idle" }));
  }, [finalizeExecution]);

  const reset = useCallback(() => {
    ++streamGenerationRef.current;
    controllerRef.current?.abort();
    finalizeExecution("cancelled", { source: "reset" });
    setState(initialState);
  }, [finalizeExecution]);

  return { ...state, execute, resume, hydratePaused, cancel, reset };
}
